import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  api,
  ApiError,
  getTour,
} from "@/lib/api";
import type {
  HouseFeed,
  HouseFeedListResponse,
  Service,
  Tour,
  Venue,
  Window,
  WindowHouseFeedAssignment,
  WindowHouseFeedAssignmentsResponse,
} from "@/lib/types";
import { LocationSelect } from "@/components/LocationSelect";
import { ShowWindowSection } from "@/components/ShowWindowSection";
import { TourAssignmentDialog } from "@/components/TourAssignmentDialog";
import { useLiveFeed } from "@/stores/liveFeed";
import { useDynoWs } from "@/hooks/useDynoWs";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
// Extracted sub-components + cells (2026-05-27 refactor).
import { MoveAcuvimDialog } from "@/components/admin-load-in/MoveAcuvimDialog";
import { HouseFeedDropdown } from "@/components/admin-load-in/HouseFeedDropdown";
import { TouringIdentityCell } from "@/components/admin-load-in/TouringIdentityCell";
import { ModelCell, LatencyCell, SystemCell, ComtradeStatusBadge } from "@/components/admin-load-in/cells";
import { meterModelLabel } from "@/lib/meter";
import type { RosterEntry } from "@/components/admin-load-in/types";

export function AdminLoadIn() {
  // Subscribe to the WS feed so the Latency column reflects fresh sample ts.
  // Without this, the Load-In page renders with a frozen liveFeed from
  // whatever the last route was, and the counter grows past every reasonable
  // bound until the next Dashboard visit.
  useDynoWs();

  const [sp, setSp] = useSearchParams();
  const windowId = sp.get("window");

  const [windows, setWindows] = useState<Window[]>([]);
  const [venues, setVenues] = useState<Venue[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [serviceEdits, setServiceEdits] = useState<Record<string, string>>({});
  const [capacityEdits, setCapacityEdits] = useState<Record<string, string>>({});
  const [savingMid, setSavingMid] = useState<string | null>(null);
  const [savingCapMid, setSavingCapMid] = useState<string | null>(null);
  const [savingFeedMid, setSavingFeedMid] = useState<string | null>(null);
  const [savingLocationMid, setSavingLocationMid] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [cleanupBusy, setCleanupBusy] = useState(false);

  // House-feed catalog for the active window's venue (U7 plan).
  const [houseFeeds, setHouseFeeds] = useState<HouseFeed[]>([]);
  // Per-membership active assignment, keyed by membership_id.
  const [feedAssignments, setFeedAssignments] = useState<
    Record<string, WindowHouseFeedAssignment>
  >({});
  const [sortKey, setSortKey] = useState<
    "system_id" | "model" | "node_id" | "latency" | "service_name" | "capacity"
  >("system_id");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  // Subscribed to the whole live-feed map so the Latency column can sort
  // by per-system ts. Re-renders this page at the snapshot cadence (~1 Hz)
  // — acceptable for an admin page with ≤ a few dozen rows, and per-row
  // children (LatencyCell, ModelCell) are already subscribing live anyway.
  const liveServices = useLiveFeed((s) => s.services);
  const [roster, setRoster] = useState<RosterEntry[]>([]);
  const [moveTarget, setMoveTarget] = useState<RosterEntry | null>(null);
  // F4 tour-assignment dialog (U8). Single boolean — the dialog reads the
  // active window and fetches its own per-membership label list on open, so
  // no parent-managed payload is needed.
  const [tourDialogOpen, setTourDialogOpen] = useState(false);
  // Tour vocabulary cache for the active window (U5). Null until the active
  // window has a tour_id AND the GET /api/tours/{id} round-trip lands. We
  // refresh after any successful vocab-add so the per-row dropdown picks
  // up the new entry without a full page reload.
  const [tour, setTour] = useState<Tour | null>(null);

  const reloadRoster = async (wid: string) => {
    try {
      const r = await api.get<{ systems: RosterEntry[] }>(
        `/api/dyno/acuvim-roster?window_id=${encodeURIComponent(wid)}`
      );
      setRoster(r.systems);
    } catch (e) {
      // Roster is supplementary — fail soft, don't blow up the page.
      console.warn("acuvim roster load failed", e);
      setRoster([]);
    }
  };

  async function onCleanupSynthetic() {
    const synth = services.filter((s) => s.system_id.startsWith("synthetic-"));
    if (synth.length === 0) return;
    if (
      !window.confirm(
        `Delete all synthetic-* data from cloud?\n\n` +
          `This removes ALL synthetic systems fleet-wide:\n` +
          `  • pq_events rows + on-disk COMTRADE files\n` +
          `  • pq_sites + auto-attached memberships\n` +
          `  • system_registry tiles\n\n` +
          `Edge SQLite still needs manual cleanup via the shell script.`
      )
    )
      return;
    setCleanupBusy(true);
    try {
      const r = await api.post<{
        pq_events_deleted: number;
        pq_sites_deleted: number;
        memberships_deleted: number;
        system_registry_deleted: number;
        files_removed: number;
      }>("/api/pq/synthetic/cleanup", {});
      alert(
        `Cleanup done.\n${r.pq_events_deleted} events, ${r.pq_sites_deleted} sites, ${r.memberships_deleted} memberships, ${r.system_registry_deleted} registry rows, ${r.files_removed} files.`
      );
      // Refresh
      if (windowId) {
        const fresh = await api.get<{ services: Service[] }>(
          `/api/services?window_id=${encodeURIComponent(windowId)}`
        );
        setServices(fresh.services);
      }
    } catch (e) {
      alert(`Cleanup failed: ${e}`);
    } finally {
      setCleanupBusy(false);
    }
  }

  async function onDeleteSyntheticMembership(s: Service) {
    if (!s.system_id.startsWith("synthetic-")) return;
    if (
      !window.confirm(
        `Delete synthetic system ${s.system_id}?\n\nRemoves the membership, ` +
          `all pq_events for this system, pq_sites row, system_registry tile, ` +
          `and on-disk COMTRADE files.`
      )
    )
      return;
    setCleanupBusy(true);
    try {
      // No per-system cleanup endpoint yet — fall back to the fleet-wide
      // cleanup which is keyed on the 'synthetic-' prefix. Effectively the
      // same outcome for a single-synthetic case, slightly heavier for
      // multi-synthetic. Trade-off accepted v1.
      const r = await api.post<{ memberships_deleted: number }>(
        "/api/pq/synthetic/cleanup",
        {}
      );
      if (windowId) {
        const fresh = await api.get<{ services: Service[] }>(
          `/api/services?window_id=${encodeURIComponent(windowId)}`
        );
        setServices(fresh.services);
      }
      // Quiet success; only alert if nothing happened.
      if (r.memberships_deleted === 0) {
        alert("No synthetic systems matched — nothing to delete.");
      }
    } catch (e) {
      alert(`Delete failed: ${e}`);
    } finally {
      setCleanupBusy(false);
    }
  }

  const activeWindow = windows.find((w) => w.window_id === windowId);

  useEffect(() => {
    Promise.all([
      api.get<{ windows: Window[] }>("/api/windows?include_closed=false"),
      api.get<{ venues: Venue[] }>("/api/venues"),
    ])
      .then(([w, v]) => {
        setWindows(w.windows);
        setVenues(v.venues);
        // Heal a stale ?window= pointing at a window not in the list (e.g. a
        // since-closed event). The dropdown only lists open windows, so it
        // can't be corrected by hand; without this the page queries the wrong
        // window and the meters show up under "Other meters" instead.
        if (
          windowId &&
          w.windows.length > 0 &&
          !w.windows.some((x) => x.window_id === windowId)
        ) {
          const sp2 = new URLSearchParams(sp);
          sp2.set("window", w.windows[0].window_id);
          setSp(sp2, { replace: true });
        }
      })
      .catch((e) => setError(String(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!windowId) return;
    api
      .get<{ services: Service[] }>(`/api/services?window_id=${encodeURIComponent(windowId)}`)
      .then((r) => {
        setServices(r.services);
        setServiceEdits({});
      })
      .catch((e) => setError(String(e)));
    reloadRoster(windowId);
  }, [windowId]);

  // House-feed catalog + per-membership active-assignment fetch. Re-fires
  // whenever the window's venue_id changes (operator switches venues, or the
  // active window itself changes). Both lookups are cheap reads.
  const currentVenueId = activeWindow?.venue_id ?? null;
  useEffect(() => {
    if (!windowId) {
      setHouseFeeds([]);
      setFeedAssignments({});
      return;
    }
    // Catalog (filtered by venue_id; empty when no venue is set).
    if (currentVenueId) {
      api
        .get<HouseFeedListResponse>(
          `/api/venues/${encodeURIComponent(currentVenueId)}/house-feeds`
        )
        .then((r) => setHouseFeeds(r.feeds))
        .catch(() => setHouseFeeds([]));
    } else {
      setHouseFeeds([]);
    }
    // Active assignments per membership.
    api
      .get<WindowHouseFeedAssignmentsResponse>(
        `/api/windows/${encodeURIComponent(windowId)}/house-feed-assignments`
      )
      .then((r) => {
        const map: Record<string, WindowHouseFeedAssignment> = {};
        for (const a of r.assignments) {
          map[a.membership_id] = a;
        }
        setFeedAssignments(map);
      })
      .catch(() => setFeedAssignments({}));
  }, [windowId, currentVenueId]);

  // Tour vocabulary fetch — fires when the active window changes or its
  // tour_id is reassigned. We fail soft (tour = null) so the page still
  // renders even if the tour was soft-deleted under us.
  const activeTourId = activeWindow?.tour_id ?? null;
  useEffect(() => {
    if (!activeTourId) {
      setTour(null);
      return;
    }
    let cancelled = false;
    getTour(activeTourId)
      .then((t) => { if (!cancelled) setTour(t); })
      .catch(() => { if (!cancelled) setTour(null); });
    return () => { cancelled = true; };
  }, [activeTourId]);

  const reloadTour = async () => {
    if (!activeTourId) return;
    try {
      const fresh = await getTour(activeTourId);
      setTour(fresh);
    } catch {
      // Best-effort refresh; the next interaction will reconcile.
    }
  };

  const reloadFeedAssignments = async () => {
    if (!windowId) return;
    try {
      const r = await api.get<WindowHouseFeedAssignmentsResponse>(
        `/api/windows/${encodeURIComponent(windowId)}/house-feed-assignments`
      );
      const map: Record<string, WindowHouseFeedAssignment> = {};
      for (const a of r.assignments) map[a.membership_id] = a;
      setFeedAssignments(map);
    } catch {
      // Best-effort refresh; the next page action will reconcile.
    }
  };

  const onServiceLocationChange = async (mid: string, label: string) => {
    setSavingLocationMid(mid);
    try {
      await api.patch(
        `/api/memberships/${encodeURIComponent(mid)}/service-location`,
        { service_location_label: label || null },
      );
      setServices((svs) =>
        svs.map((s) =>
          s.membership_id === mid
            ? { ...s, service_location_label: label || null }
            : s,
        ),
      );
    } catch (e) {
      if (e instanceof ApiError && e.status === 400) {
        const body = e.body as { error?: string };
        alert(`Couldn't set location: ${body?.error ?? "validation failed"}`);
      } else {
        alert(`Failed to set service location: ${e}`);
      }
    } finally {
      setSavingLocationMid(null);
    }
  };

  const onHouseFeedChange = async (mid: string, feedId: string) => {
    setSavingFeedMid(mid);
    try {
      await api.patch(`/api/memberships/${encodeURIComponent(mid)}/house-feed`, {
        feed_id: feedId || null,
      });
      await reloadFeedAssignments();
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        const body = e.body as
          | { error?: string; message?: string; claimed_by_system_id?: string }
          | undefined;
        if (body?.error === "feed_already_assigned") {
          alert(
            `That house feed is already assigned to ${body.claimed_by_system_id ?? "another service"}. `
            + "Clear the existing assignment first."
          );
        } else {
          alert(
            "Another assignment change is in flight for this membership — refresh and retry."
          );
        }
      } else if (e instanceof ApiError && e.status === 400) {
        const body = e.body as { error?: string };
        alert(`Assignment rejected: ${body?.error ?? "validation failed"}`);
      } else {
        alert(`Failed to set house feed: ${e}`);
      }
    } finally {
      setSavingFeedMid(null);
    }
  };

  const onVenueChange = async (venueId: string | null) => {
    if (!windowId) return;
    try {
      await api.patch(`/api/windows/${encodeURIComponent(windowId)}`, { venue_id: venueId });
      setWindows((ws) =>
        ws.map((w) => (w.window_id === windowId ? { ...w, venue_id: venueId } : w))
      );
    } catch (e) {
      alert(`Failed to set venue: ${e}`);
    }
  };

  const onSaveName = async (mid: string) => {
    const name = serviceEdits[mid];
    setSavingMid(mid);
    try {
      await api.patch(`/api/memberships/${encodeURIComponent(mid)}/service`, {
        service_name: name || null,
      });
      setServices((svs) =>
        svs.map((s) => (s.membership_id === mid ? { ...s, service_name: name || null } : s))
      );
      setServiceEdits((e) => {
        const next = { ...e };
        delete next[mid];
        return next;
      });
    } catch (e) {
      alert(`Save failed: ${e}`);
    } finally {
      setSavingMid(null);
    }
  };

  const onSaveCapacity = async (mid: string) => {
    const raw = (capacityEdits[mid] ?? "").trim();
    let amps: number | null;
    if (raw === "") {
      amps = null;
    } else {
      const n = Number(raw);
      if (!Number.isInteger(n) || n < 1 || n > 6000) {
        alert("Capacity must be an integer between 1 and 6000 amps, or blank to clear.");
        return;
      }
      amps = n;
    }
    setSavingCapMid(mid);
    try {
      await api.patch(`/api/memberships/${encodeURIComponent(mid)}/capacity`, {
        service_capacity_amps: amps,
      });
      setServices((svs) =>
        svs.map((s) =>
          s.membership_id === mid ? { ...s, service_capacity_amps: amps } : s
        )
      );
      setCapacityEdits((e) => {
        const next = { ...e };
        delete next[mid];
        return next;
      });
    } catch (e) {
      alert(`Save failed: ${e}`);
    } finally {
      setSavingCapMid(null);
    }
  };

  // Sort helpers — driven by header clicks. Latency is special: ascending
  // means "freshest first" (low age), which is `ts` descending — so we
  // compare ts then invert before applying sortDir.
  const toggleSort = (key: typeof sortKey) => {
    if (key === sortKey) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortDir("asc"); }
  };
  const cmpValue = (s: Service): [number, string | number] => {
    if (sortKey === "system_id") return [0, s.system_id.toLowerCase()];
    if (sortKey === "node_id") return [0, (s.node_id ?? "￿").toLowerCase()];
    if (sortKey === "service_name") return [0, (s.service_name ?? "￿").toLowerCase()];
    if (sortKey === "capacity") return [1, s.service_capacity_amps ?? -Infinity];
    if (sortKey === "model") {
      const live = liveServices[s.system_id]?.meter_model ?? s.meter_model ?? null;
      return [0, (meterModelLabel(live) ?? "￿").toLowerCase()];
    }
    // latency — invert via negation so asc → freshest first
    return [1, -(liveServices[s.system_id]?.ts ?? 0)];
  };
  const sortedServices = [...services].sort((a, b) => {
    const [ka, va] = cmpValue(a);
    const [kb, vb] = cmpValue(b);
    void ka; void kb;
    const cmp = typeof va === "number" && typeof vb === "number"
      ? va - vb
      : String(va).localeCompare(String(vb));
    return sortDir === "asc" ? cmp : -cmp;
  });
  const SortHeader = ({ label, k, className }: { label: string; k: typeof sortKey; className?: string }) => {
    const active = sortKey === k;
    return (
      <TableHead className={className}>
        <button
          type="button"
          className="inline-flex items-center gap-1 hover:text-foreground"
          onClick={() => toggleSort(k)}
        >
          {label}
          <span className={cn("text-[9px] leading-none", active ? "" : "opacity-30")}>
            {active ? (sortDir === "asc" ? "▲" : "▼") : "↕"}
          </span>
        </button>
      </TableHead>
    );
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Load-in</h1>
        <p className="text-sm text-muted-foreground">
          Name the Acuvim services for a show and link the event to a venue
        </p>
      </div>

      {error && <div className="text-destructive text-sm">{error}</div>}

      <Card>
        <CardHeader>
          <CardTitle>Event</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap items-center gap-3">
          <select
            value={windowId ?? ""}
            onChange={(e) => {
              const sp2 = new URLSearchParams(sp);
              sp2.set("window", e.target.value);
              setSp(sp2);
            }}
            className="h-9 rounded-md border border-input bg-transparent px-3 text-sm"
          >
            <option value="" disabled>
              — select event —
            </option>
            {windows.map((w) => (
              <option key={w.window_id} value={w.window_id}>
                {w.name}
              </option>
            ))}
          </select>

          {activeWindow && (
            <>
              <span className="text-sm text-muted-foreground">Venue:</span>
              <select
                value={activeWindow.venue_id ?? ""}
                onChange={(e) => onVenueChange(e.target.value || null)}
                className="h-9 rounded-md border border-input bg-transparent px-3 text-sm"
              >
                <option value="">— none —</option>
                {venues.map((v) => (
                  <option key={v.venue_id} value={v.venue_id}>
                    {v.name}
                  </option>
                ))}
              </select>

              {/* F4 tour-assignment entry point (U8). Label flips based on
                  whether a tour is already linked — "Assign" creates the
                  first link, "Re-assign" replaces an existing one. */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setTourDialogOpen(true)}
              >
                {activeWindow.tour_id ? "Re-assign tour…" : "Assign to tour…"}
              </Button>
            </>
          )}
        </CardContent>
      </Card>

      {tourDialogOpen && activeWindow && (
        <TourAssignmentDialog
          window={activeWindow}
          open={tourDialogOpen}
          onClose={() => setTourDialogOpen(false)}
          onAssigned={async () => {
            // Reload windows (so activeWindow.tour_id reflects the new link
            // → the per-row TouringIdentity dropdown column from U5 appears)
            // AND services (so the just-tagged touring_identity values render).
            try {
              const [w, s] = await Promise.all([
                api.get<{ windows: Window[] }>(
                  "/api/windows?include_closed=false",
                ),
                windowId
                  ? api.get<{ services: Service[] }>(
                      `/api/services?window_id=${encodeURIComponent(windowId)}`,
                    )
                  : Promise.resolve({ services: [] as Service[] }),
              ]);
              setWindows(w.windows);
              setServices(s.services);
            } catch {
              // Best-effort refresh — the next interaction reconciles.
            }
          }}
        />
      )}

      {windowId && (
        <ShowWindowSection
          windowId={windowId}
          venue={
            activeWindow?.venue_id
              ? venues.find((v) => v.venue_id === activeWindow.venue_id) ?? null
              : null
          }
        />
      )}

      {windowId && (() => {
        const inEvent = new Set(services.map((s) => s.system_id));
        const otherMeters = roster.filter((r) => !inEvent.has(r.system_id));
        return otherMeters.length > 0 ? (
          <Card>
            <CardHeader>
              <CardTitle>
                Other meters ({otherMeters.length})
              </CardTitle>
              <p className="text-xs text-muted-foreground mt-1">
                Acuvims known to the fleet but not currently in this event. Move
                one here to rebind without going through the field map.
              </p>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>System</TableHead>
                    <TableHead>Node</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="w-24 text-right" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {otherMeters.map((r) => {
                    let statusLabel: string;
                    if (!r.online) {
                      statusLabel = "Offline";
                    } else if (r.other_event) {
                      statusLabel = r.other_event.redacted
                        ? "Bound to another event"
                        : `Bound to ${r.other_event.window_name ?? "another event"}`;
                    } else {
                      statusLabel = "Unassigned";
                    }
                    const actionLabel = r.other_event ? "Move here" : "Add here";
                    return (
                      <TableRow key={r.system_id} className="text-muted-foreground">
                        <TableCell className="font-mono text-xs">
                          {r.system_id}
                          <ComtradeStatusBadge status={r.comtrade_url_status} />
                        </TableCell>
                        <TableCell className="text-xs">{r.node_id ?? "—"}</TableCell>
                        <TableCell className="text-xs">{statusLabel}</TableCell>
                        <TableCell className="text-right">
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={r.other_event?.redacted}
                            onClick={() => setMoveTarget(r)}
                          >
                            {actionLabel}
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        ) : null;
      })()}

      {moveTarget && activeWindow && windowId && (
        <MoveAcuvimDialog
          target={moveTarget}
          windowId={windowId}
          windowName={activeWindow.name}
          onClose={() => setMoveTarget(null)}
          onDone={async () => {
            setMoveTarget(null);
            const fresh = await api.get<{ services: Service[] }>(
              `/api/services?window_id=${encodeURIComponent(windowId)}`
            );
            setServices(fresh.services);
            reloadRoster(windowId);
          }}
        />
      )}

      {windowId && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-4">
            <CardTitle>
              Acuvim services ({services.length})
            </CardTitle>
            {services.some((s) => s.system_id.startsWith("synthetic-")) && (
              <Button
                size="sm"
                variant="destructive"
                disabled={cleanupBusy}
                onClick={onCleanupSynthetic}
                title="Wipe all synthetic-* events, sites, memberships, and system_registry rows from cloud"
              >
                {cleanupBusy ? "Cleaning…" : "Delete all synthetics"}
              </Button>
            )}
          </CardHeader>
          <CardContent>
            {services.length === 0 ? (
              <div className="text-sm text-muted-foreground py-2">
                No Acuvim meters bound to this event yet. Commission meters via the
                RPM field map first — Dyno reads the memberships list.
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <SortHeader label="System" k="system_id" />
                    <SortHeader label="Model" k="model" />
                    <SortHeader label="Node" k="node_id" />
                    <SortHeader label="Latency" k="latency" className="w-16" />
                    <SortHeader label="Service name" k="service_name" />
                    <TableHead className="w-20" />
                    <SortHeader label="Capacity (A)" k="capacity" className="w-28" />
                    <TableHead className="w-20" />
                    <TableHead className="w-44">Service location</TableHead>
                    {activeTourId && (
                      <TableHead className="w-56">Touring identity</TableHead>
                    )}
                    <TableHead className="w-48">House feed</TableHead>
                    <TableHead className="w-8" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedServices.map((s) => {
                    const editVal = serviceEdits[s.membership_id];
                    const current = editVal !== undefined ? editVal : s.service_name ?? "";
                    const dirty = editVal !== undefined && editVal !== (s.service_name ?? "");
                    const capEditVal = capacityEdits[s.membership_id];
                    const capCurrentStored = s.service_capacity_amps != null ? String(s.service_capacity_amps) : "";
                    const capCurrent = capEditVal !== undefined ? capEditVal : capCurrentStored;
                    const capDirty = capEditVal !== undefined && capEditVal !== capCurrentStored;
                    const isSynth = s.system_id.startsWith("synthetic-");
                    return (
                      <TableRow key={s.membership_id}>
                        <TableCell className="font-mono text-xs">
                          <SystemCell s={s} isSynth={isSynth} />
                        </TableCell>
                        <TableCell>
                          <ModelCell systemId={s.system_id} staticModel={s.meter_model} />
                        </TableCell>
                        <TableCell className="text-xs">{s.node_id ?? "—"}</TableCell>
                        <TableCell>
                          <LatencyCell systemId={s.system_id} />
                        </TableCell>
                        <TableCell>
                          <Input
                            value={current}
                            placeholder="e.g. FOH L Main"
                            onChange={(e) =>
                              setServiceEdits((edits) => ({
                                ...edits,
                                [s.membership_id]: e.target.value,
                              }))
                            }
                          />
                        </TableCell>
                        <TableCell>
                          <Button
                            size="sm"
                            variant={dirty ? "default" : "ghost"}
                            disabled={!dirty || savingMid === s.membership_id}
                            onClick={() => onSaveName(s.membership_id)}
                          >
                            {savingMid === s.membership_id ? "…" : "Save"}
                          </Button>
                        </TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            min={1}
                            max={6000}
                            step={1}
                            value={capCurrent}
                            placeholder="Amps"
                            onChange={(e) =>
                              setCapacityEdits((edits) => ({
                                ...edits,
                                [s.membership_id]: e.target.value,
                              }))
                            }
                          />
                        </TableCell>
                        <TableCell>
                          <Button
                            size="sm"
                            variant={capDirty ? "default" : "ghost"}
                            disabled={!capDirty || savingCapMid === s.membership_id}
                            onClick={() => onSaveCapacity(s.membership_id)}
                          >
                            {savingCapMid === s.membership_id ? "…" : "Save"}
                          </Button>
                        </TableCell>
                        <TableCell>
                          <LocationSelect
                            value={s.service_location_label ?? ""}
                            disabled={savingLocationMid === s.membership_id}
                            onChange={(label) =>
                              onServiceLocationChange(s.membership_id, label)
                            }
                          />
                        </TableCell>
                        {activeTourId && (
                          <TableCell>
                            <TouringIdentityCell
                              tourId={activeTourId}
                              tour={tour}
                              service={s}
                              onTagged={(identity) => {
                                setServices((svs) =>
                                  svs.map((x) =>
                                    x.membership_id === s.membership_id
                                      ? { ...x, touring_identity: identity }
                                      : x,
                                  ),
                                );
                              }}
                              onVocabRefresh={reloadTour}
                            />
                          </TableCell>
                        )}
                        <TableCell>
                          <HouseFeedDropdown
                            venueId={currentVenueId}
                            feeds={houseFeeds}
                            serviceLocation={s.service_location_label ?? null}
                            currentAssignment={feedAssignments[s.membership_id] ?? null}
                            allAssignments={feedAssignments}
                            membershipId={s.membership_id}
                            saving={savingFeedMid === s.membership_id}
                            onChange={(feedId) =>
                              onHouseFeedChange(s.membership_id, feedId)
                            }
                          />
                        </TableCell>
                        <TableCell className="text-right">
                          {isSynth && (
                            <button
                              type="button"
                              className="text-muted-foreground hover:text-destructive text-sm"
                              title="Delete this synthetic system + all its data"
                              disabled={cleanupBusy}
                              onClick={() => onDeleteSyntheticMembership(s)}
                            >
                              ✕
                            </button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

